import { markRaw } from 'vue'
import type { TreeOption } from 'naive-ui'
import MapInfo from '@/model/MapInfo'

export type StoryTreeNode = TreeOption & {
  key: number
  label: string
  children?: StoryTreeNode[]
}

/** 剧情 ID 展示：与旧版工具一致，至少三位补零（仅用于 UI，不影响存储值）。 */
export function formatStoryMapId(mapId: number): string {
  const id = Math.max(0, Math.round(Number(mapId) || 0))
  return String(id).padStart(3, '0')
}

/** 剧情选择框等单行展示：`003：第一章`。 */
export function formatStoryMapSelectLabel(map: MapInfo): string {
  const name = String(map.Name ?? '').trim() || '未命名剧情'
  return `${formatStoryMapId(map.Id)}：${name}`
}

/** 场景树仅传 key + label，避免挂 MapInfo 导致大量响应式开销。 */
/** 构建场景树节点标题。 */
export function sceneTreeLabel(map: MapInfo): string {
  const name = map.Name?.trim() ? map.Name : '未命名'
  return `${formatStoryMapId(map.Id)}  ${name}`
}

/** 按父节点和 Order 字段稳定排序剧情列表。 */
export function sortStoryMaps(list: MapInfo[]): MapInfo[] {
  return [...list].sort((a, b) => {
    if (a.Order !== b.Order) return a.Order - b.Order
    return a.Id - b.Id
  })
}

/** 递归构建 n-tree 节点数据，不携带 MapInfo 引用。 */
export function mapInfoSubtreeToStoryNodes(maps: MapInfo[]): StoryTreeNode[] {
  return maps.map((m) => {
    const children =
      m.Childs?.length > 0 ? mapInfoSubtreeToStoryNodes(m.Childs) : undefined
    return markRaw(children?.length
      ? { key: m.Id, label: sceneTreeLabel(m), children }
      : { key: m.Id, label: sceneTreeLabel(m) })
  })
}

/** 按关键词筛选剧情树，保留匹配节点及其祖先链。 */
export function filterStoryTree(nodes: StoryTreeNode[], q: string): StoryTreeNode[] {
  const out = nodes.flatMap((node) => {
    const children = node.children?.length ? filterStoryTree(node.children, q) : []
    const matched =
      String(node.key).includes(q) ||
      (typeof node.label === 'string' && node.label.toLowerCase().includes(q))
    if (!matched && !children.length) return []
    return [
      markRaw({
        key: node.key,
        label: node.label,
        ...(children.length ? { children } : {}),
      }),
    ]
  })
  return out
}

function buildNormalizedStoryParentById(maps: MapInfo[]): Map<number, number> {
  const allIds = new Set(maps.map((map) => map.Id))
  const parentById = new Map<number, number>()
  for (const map of maps) {
    const parentId =
      map.ParentId <= 0 || map.ParentId === map.Id || !allIds.has(map.ParentId)
        ? 0
        : map.ParentId
    parentById.set(map.Id, parentId)
  }
  return parentById
}

/** 剧情选择器：展开选中节点的祖先链（含自身，便于显示子剧情）。 */
export function collectStoryPickerExpandKeys(sceneId: number, maps: MapInfo[]): number[] {
  const id = Math.round(Number(sceneId) || 0)
  if (id <= 0 || !maps.some((item) => Number(item.Id) === id)) return []
  const parentById = buildNormalizedStoryParentById(maps)
  const keys: number[] = []
  let cursor = id
  let guard = 0
  while (guard < 2000) {
    keys.push(cursor)
    const parentId = parentById.get(cursor)
    if (!parentId || parentId <= 0 || parentId === cursor) break
    cursor = parentId
    guard += 1
  }
  return keys
}

/** 判断剧情 ID 是否存在于当前工程剧情列表中。 */
export function isStoryMapIdInList(mapId: number, maps: MapInfo[]): boolean {
  const id = Math.round(Number(mapId) || 0)
  if (id <= 0) return false
  return maps.some((item) => Number(item.Id) === id)
}
